import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import api from "../lib/api";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaBookOpen,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaMoneyBillWave,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaTimes,
  FaWallet,
} from "react-icons/fa";
import "./CSS/CustomerCreditPremium.css";

const paymentModes = ["CASH", "UPI", "CARD", "NEFT", "RTGS"];
const fuelOptions = [
  "Petrol",
  "Diesel",
  "Power Petrol",
  "Power Diesel",
  "CNG",
  "Diesel 100",
  "Opening Balance",
];
const pageSize = 5;

function toDateOnly(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseSafeDate(value) {
  if (!value) return null;
  const normalized = String(value).replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = parseSafeDate(value) || new Date(value || Date.now());
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function money(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaWallet;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function PassbookTransaction({ row }) {
  const isPayment = String(row.source_type || "").toUpperCase() === "PAYMENT";

  return (
    <div className={`cc-transaction ${isPayment ? "payment" : "debit"}`}>
      <div className="cc-transaction-head">
        <div>
          <strong>{row.transaction_type || "ENTRY"}</strong>
          <span>{row.description || row.comment || "-"}</span>
        </div>
        <div className="cc-transaction-meta">
          <span>{formatDateTime(row.transaction_datetime)}</span>
          {row.payment_mode ? <em>{row.payment_mode}</em> : null}
        </div>
      </div>

      <div className="cc-transaction-amounts">
        <span className={`cc-pill ${isPayment ? "credit" : "debit"}`}>
          {isPayment ? `Credit ${money(row.credit)}` : `Debit ${money(row.debit)}`}
        </span>
        <span className="cc-balance-badge">Balance {money(row.balance)}</span>
      </div>
    </div>
  );
}

export default function Customercredit() {
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [customers, setCustomers] = useState([]);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passbookCustomerId, setPassbookCustomerId] = useState("");
  const [passbookRange, setPassbookRange] = useState({
    start: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });
  const [passbookPage, setPassbookPage] = useState(1);
  const [passbookData, setPassbookData] = useState({
    opening_balance: 0,
    transactions: [],
    summary: {
      total_debit: 0,
      total_credit: 0,
      closing_balance: 0,
    },
  });
  const [initialForm, setInitialForm] = useState({
    customer_id: "",
    amount: "",
    fuel_type: "Opening Balance",
    volume: "",
    comment: "Opening balance",
    created_at: new Date(),
  });
  const [settlementForm, setSettlementForm] = useState({
    customer_id: "",
    amount_paid: "",
    payment_mode: "CASH",
    reference_no: "",
    comment: "",
    payment_date: new Date(),
  });
  const [creditBlock, setCreditBlock] = useState({
    has_limit: false,
    running_credit: 0,
    max_credit_amount: null,
    available_credit: null,
    limit: null,
  });
  const [openingCreditBlock, setOpeningCreditBlock] = useState({
    has_limit: false,
    running_credit: 0,
    max_credit_amount: null,
    available_credit: null,
    limit: null,
  });
  const [creditLimitInput, setCreditLimitInput] = useState("");
  const [creditLimitSaving, setCreditLimitSaving] = useState(false);

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      type,
      title,
      message,
    });
  };

  const dedupeCustomers = (rows) => {
    const seen = new Set();
    return rows
      .filter((row) => row.id !== undefined && row.id !== null)
      .sort((a, b) => {
        const aTime = new Date(a.created_at || a.last_activity_at || 0).getTime();
        const bTime = new Date(b.created_at || b.last_activity_at || 0).getTime();
        if (bTime !== aTime) return bTime - aTime;
        return Number(b.id || 0) - Number(a.id || 0);
      })
      .filter((row) => {
        const key = String(row.name || "")
          .trim()
          .toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get("/api/users?scope=tenant", { headers: authHeaders });
      const rows = Array.isArray(res.data) ? res.data : [];
      const normalized = dedupeCustomers(
        rows
        .filter((row) => String(row.role || "").toLowerCase() === "customer")
        .map((row) => ({
          id: row.id,
          name: row.name,
          phone: row.phone || "",
          last_activity_at: row.created_at || null,
          created_at: row.created_at || null,
        }))
      );

      if (normalized.length) {
        setCustomers(normalized);
        return normalized;
      }
    } catch (err) {
      console.error("Tenant customer load failed:", err);
    }

    const fallback = await api.get("/api/users/customers", { headers: authHeaders });
    const rows = Array.isArray(fallback.data) ? fallback.data : [];
    const normalized = dedupeCustomers(
      rows
      .map((row) => ({
        id: row.id,
        name: row.name,
        phone: row.phone || "",
        last_activity_at: row.created_at || null,
        created_at: row.created_at || null,
      }))
    );
    setCustomers(normalized);
    return normalized;
  };

  const fetchPassbook = async () => {
    if (!passbookCustomerId) {
      setPassbookData({
        opening_balance: 0,
        transactions: [],
        summary: {
          total_debit: 0,
          total_credit: 0,
          closing_balance: 0,
        },
      });
      return;
    }

    const res = await api.get("/api/customer-passbook", {
      headers: authHeaders,
      params: {
        customerId: passbookCustomerId,
        startDate: toDateOnly(passbookRange.start),
        endDate: toDateOnly(passbookRange.end),
      },
    });

    setPassbookData(res.data || {});
  };

  const fetchCreditBlock = async (customerId = passbookCustomerId) => {
    if (!customerId) {
      setCreditBlock({
        has_limit: false,
        running_credit: 0,
        max_credit_amount: null,
        available_credit: null,
        limit: null,
      });
      setCreditLimitInput("");
      return;
    }

    const res = await api.get("/api/users/customercredit/limit", {
      headers: authHeaders,
      params: { customer_id: customerId },
    });

    const data = res.data || {};
    setCreditBlock(data);
    setCreditLimitInput(
      data.has_limit && data.max_credit_amount !== null && data.max_credit_amount !== undefined
        ? String(Number(data.max_credit_amount).toFixed(2))
        : ""
    );
  };

  const fetchOpeningCreditBlock = async (customerId) => {
    if (!customerId) {
      setOpeningCreditBlock({
        has_limit: false,
        running_credit: 0,
        max_credit_amount: null,
        available_credit: null,
        limit: null,
      });
      return;
    }

    const res = await api.get("/api/users/customercredit/limit", {
      headers: authHeaders,
      params: { customer_id: customerId },
    });

    setOpeningCreditBlock(res.data || {});
  };

  const saveCreditLimit = async () => {
    if (!passbookCustomerId) {
      showNotice("warning", "Customer required", "Please select a customer first.");
      return;
    }

    try {
      setCreditLimitSaving(true);
      await api.post(
        "/api/users/customercredit/limit",
        {
          customer_id: passbookCustomerId,
          max_credit_amount: creditLimitInput === "" ? null : Number(creditLimitInput),
        },
        { headers: authHeaders }
      );
      showNotice("success", "Credit block updated", "The customer's credit limit was saved.");
      await fetchCreditBlock(passbookCustomerId);
    } catch (err) {
      console.error("Credit block save error:", err);
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save the credit limit.");
    } finally {
      setCreditLimitSaving(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        await fetchCustomers();
      } catch (err) {
        console.error("Customer credit bootstrap error:", err);
        showNotice("error", "Load failed", "Unable to load customer credit data.");
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5200 : 3600
    );
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (customers.length && !passbookCustomerId) {
      const firstCustomerId = String(customers[0].id);
      setPassbookCustomerId(firstCustomerId);
      setInitialForm((prev) => ({ ...prev, customer_id: firstCustomerId }));
      setSettlementForm((prev) => ({ ...prev, customer_id: firstCustomerId }));
    }
  }, [customers, passbookCustomerId]);

  useEffect(() => {
    if (!passbookCustomerId) return undefined;
    Promise.all([fetchPassbook(), fetchCreditBlock(passbookCustomerId)]).catch((err) => {
      console.error("Passbook load error:", err);
      showNotice(
        "error",
        "Passbook failed",
        err?.response?.data?.message || "Unable to load customer passbook."
      );
    });
    return undefined;
  }, [passbookCustomerId, passbookRange.start, passbookRange.end]);

  useEffect(() => {
    setPassbookPage(1);
  }, [passbookCustomerId, passbookRange.start, passbookRange.end]);

  const passbookCustomer = customers.find((c) => String(c.id) === String(passbookCustomerId));
  const passbookTransactions = passbookData.transactions || [];
  const passbookSummary = useMemo(() => {
    const totalDebit = Number(passbookData.summary?.total_debit || 0);
    const totalCredit = Number(passbookData.summary?.total_credit || 0);
    const openingBalance = Number(passbookData.opening_balance || 0);
    const closingBalance = Number(passbookData.summary?.closing_balance || 0);

    return {
      opening_balance: openingBalance,
      total_debit: totalDebit,
      total_credit: totalCredit,
      closing_balance: closingBalance,
      count: passbookTransactions.length,
    };
  }, [passbookData, passbookTransactions.length]);

  const passbookTotalPages = Math.max(Math.ceil(passbookTransactions.length / pageSize), 1);
  const safePassbookPage = Math.min(passbookPage, passbookTotalPages);
  const passbookRows = passbookTransactions.slice(
    (safePassbookPage - 1) * pageSize,
    safePassbookPage * pageSize
  );

  useEffect(() => {
    setPassbookPage((prev) => Math.min(prev, passbookTotalPages));
  }, [passbookTotalPages]);

  const getPassbookPageNumbers = () => {
    const maxButtons = 5;
    if (passbookTotalPages <= maxButtons) {
      return Array.from({ length: passbookTotalPages }, (_, i) => i + 1);
    }

    let start = Math.max(1, safePassbookPage - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > passbookTotalPages) {
      end = passbookTotalPages;
      start = end - maxButtons + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const quickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    setPassbookRange({ start, end });
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchPassbook(), fetchCreditBlock()]);
      showNotice("success", "Refreshed", "Customer credit data is up to date.");
    } catch (err) {
      console.error("Refresh error:", err);
      showNotice("error", "Refresh failed", "Unable to refresh customer credit data.");
    } finally {
      setLoading(false);
    }
  };

  const saveInitialCredit = async () => {
    const selectedCustomerId = initialForm.customer_id;

    if (!selectedCustomerId) {
      showNotice("warning", "Customer required", "Please select a customer for the opening balance.");
      return;
    }

    if (!initialForm.amount || Number(initialForm.amount) <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid opening balance.");
      return;
    }

    const amount = Number(initialForm.amount);
    if (
      openingCreditBlock?.has_limit &&
      Number(openingCreditBlock.max_credit_amount || 0) > 0 &&
      Number(openingCreditBlock.running_credit || 0) + amount >
        Number(openingCreditBlock.max_credit_amount || 0) + 0.00001
    ) {
      showNotice(
        "warning",
        "Credit block exceeded",
        `This customer can only run up to Rs ${Number(openingCreditBlock.max_credit_amount).toFixed(
          2
        )}. Current running credit is Rs ${Number(openingCreditBlock.running_credit || 0).toFixed(
          2
        )}, so this entry would cross the limit.`
      );
      return;
    }

    try {
      setSaving(true);
      await api.post(
        "/api/users/customercredit/create",
        {
          customer_id: initialForm.customer_id,
          amount,
          fuel_type: initialForm.fuel_type,
          volume: initialForm.volume ? Number(initialForm.volume) : null,
          comment: initialForm.comment || "Opening balance",
          created_at: formatDateTime(initialForm.created_at),
        },
        { headers: authHeaders }
      );

      showNotice("success", "Opening balance added", "The opening credit has been saved.");
      setInitialForm({
        customer_id: passbookCustomerId || "",
        amount: "",
        fuel_type: "Opening Balance",
        volume: "",
        comment: "Opening balance",
        created_at: new Date(),
      });
      await Promise.all([
        fetchPassbook(),
        fetchCreditBlock(passbookCustomerId),
        fetchOpeningCreditBlock(selectedCustomerId),
      ]);
    } catch (err) {
      console.error("Opening balance save error:", err);
      showNotice(
        "error",
        "Save failed",
        err?.response?.data?.error || "Unable to save the opening balance."
      );
    } finally {
      setSaving(false);
    }
  };

  const settleLedger = async () => {
    if (!settlementForm.customer_id) {
      showNotice("warning", "Customer required", "Please select the customer before settling.");
      return;
    }

    if (!settlementForm.amount_paid || Number(settlementForm.amount_paid) <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid settlement amount.");
      return;
    }

    try {
      setSaving(true);
      await api.post(
        "/api/users/customercredit/bulk-settle",
        {
          customer_id: settlementForm.customer_id,
          amount_paid: Number(settlementForm.amount_paid),
          payment_mode: settlementForm.payment_mode,
          reference_no: settlementForm.reference_no,
          comment: settlementForm.comment,
          payment_date: formatDateTime(settlementForm.payment_date),
          credit_ids: [],
        },
        { headers: authHeaders }
      );

      showNotice("success", "Payment applied", "The payment was allocated to the oldest pending credits.");
      setSettlementForm((prev) => ({
        ...prev,
        amount_paid: "",
        reference_no: "",
        comment: "",
        payment_date: new Date(),
      }));
      await Promise.all([fetchPassbook(), fetchCreditBlock(passbookCustomerId)]);
    } catch (err) {
      console.error("Settlement error:", err);
      showNotice(
        "error",
        "Settlement failed",
        err?.response?.data?.error || "Unable to settle the payment right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const selectCustomer = (customerId) => {
    const value = String(customerId || "");
    setPassbookCustomerId(value);
    setSettlementForm((prev) => ({ ...prev, customer_id: value }));
    setInitialForm((prev) => ({ ...prev, customer_id: value }));
    fetchOpeningCreditBlock(value).catch((err) => console.error("Opening credit block fetch error:", err));
  };

  return (
    <div className="cc-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Finance Control</div>
          <h2>Passbook-style customer credit settlement.</h2>
          <p>
            Settle bulk payments against the oldest pending credits, keep the ledger running in
            passbook order, and avoid the noisy report grid on this screen.
          </p>
          <div className="cc-hero-meta">
            <span>{customers.length} customers</span>
            <span>{money(passbookSummary.closing_balance)} closing balance</span>
            <span>{passbookSummary.count} ledger rows</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={refreshAll} disabled={loading}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
        </div>
      </section>

      <section className="cc-context-strip">
        <div className="cc-context-head">
          <div>
            <span>Ledger context</span>
            <h3>Choose customer and date range first</h3>
          </div>
          <div className="cc-context-chip">
            <strong>{passbookCustomer?.name || "Selected customer"}</strong>
            <span>{money(passbookSummary.closing_balance)} closing balance</span>
          </div>
        </div>

        <div className="cc-context-grid">
          <div>
            <label className="form-label">Customer</label>
            <select
              className="form-select"
              value={passbookCustomerId}
              onChange={(e) => selectCustomer(e.target.value)}
            >
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Start date</label>
            <DatePicker
              className="form-control"
              selected={passbookRange.start}
              onChange={(d) => setPassbookRange({ ...passbookRange, start: d || new Date() })}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>

          <div>
            <label className="form-label">End date</label>
            <DatePicker
              className="form-control"
              selected={passbookRange.end}
              onChange={(d) => setPassbookRange({ ...passbookRange, end: d || new Date() })}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>
        </div>

        <div className="cc-passbook-toolbar">
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(1)}>
            1D
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(7)}>
            1W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(14)}>
            2W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(30)}>
            30D
          </button>
        </div>

        <div className="cc-credit-block">
          <div className="cc-credit-block-head">
            <div>
              <span>Credit block</span>
              <h4>Set maximum credit allowed for this customer</h4>
            </div>
            <div className={`cc-credit-block-chip ${creditBlock.has_limit ? "active" : ""}`}>
              <strong>{creditBlock.has_limit ? "Active" : "No limit"}</strong>
              <span>
                {creditBlock.has_limit
                  ? `Available ${money(creditBlock.available_credit)}`
                  : "Credit can be given without a cap"}
              </span>
            </div>
          </div>

          <div className="cc-credit-block-grid">
            <div className="cc-credit-stat">
              <span>Running credit</span>
              <strong>{money(creditBlock.running_credit)}</strong>
            </div>
            <div className="cc-credit-stat">
              <span>Maximum allowed</span>
              <strong>
                {creditBlock.has_limit ? money(creditBlock.max_credit_amount) : "-"}
              </strong>
            </div>
            <div className="cc-credit-limit-form">
              <label className="form-label">Credit limit</label>
              <div className="cc-credit-limit-row">
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  placeholder="Enter max allowed credit"
                  value={creditLimitInput}
                  onChange={(e) => setCreditLimitInput(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={saveCreditLimit}
                  disabled={creditLimitSaving}
                >
                  {creditLimitSaving ? "Saving..." : "Save limit"}
                </button>
              </div>
              <small className="text-muted">
                Leave it blank and save to remove the block for this customer.
              </small>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-summary-grid">
        <StatCard
          tone="blue"
          icon={FaMoneyBillWave}
          label="Opening balance"
          value={money(passbookSummary.opening_balance)}
          caption="Balance brought into this range"
        />
        <StatCard
          tone="green"
          icon={FaWallet}
          label="Debit total"
          value={money(passbookSummary.total_debit)}
          caption="Credits added to the ledger"
        />
        <StatCard
          tone="amber"
          icon={FaCheckCircle}
          label="Credit total"
          value={money(passbookSummary.total_credit)}
          caption="Payments applied in FIFO order"
        />
        <StatCard
          tone="slate"
          icon={FaClock}
          label="Closing balance"
          value={money(passbookSummary.closing_balance)}
          caption={`${passbookSummary.count} passbook entries`}
        />
      </section>

      <section className="cc-grid">
        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Passbook settlement</span>
              <h3>Apply a bulk payment without touching line items</h3>
            </div>
            <div className="cc-ledger-chip">
              <strong>FIFO</strong>
              <span>Oldest pending first</span>
            </div>
          </div>

          <div className="cc-form-grid">
            <div>
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={settlementForm.customer_id}
                onChange={(e) => selectCustomer(e.target.value)}
              >
                <option value="">Select customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Payment date</label>
              <DatePicker
                className="form-control"
                selected={settlementForm.payment_date}
                onChange={(d) => setSettlementForm({ ...settlementForm, payment_date: d || new Date() })}
                showTimeSelect
                dateFormat="yyyy-MM-dd HH:mm"
                popperPlacement="bottom-start"
              />
            </div>

            <div>
              <label className="form-label">Amount</label>
              <input
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                placeholder="Settlement amount"
                value={settlementForm.amount_paid}
                onChange={(e) => setSettlementForm({ ...settlementForm, amount_paid: e.target.value })}
              />
            </div>

            <div>
              <label className="form-label">Payment mode</label>
              <select
                className="form-select"
                value={settlementForm.payment_mode}
                onChange={(e) => setSettlementForm({ ...settlementForm, payment_mode: e.target.value })}
              >
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Reference no</label>
              <input
                className="form-control"
                placeholder="UPI, cheque, bank ref"
                value={settlementForm.reference_no}
                onChange={(e) => setSettlementForm({ ...settlementForm, reference_no: e.target.value })}
              />
            </div>

            <div>
              <label className="form-label">Comment</label>
              <input
                className="form-control"
                placeholder="Optional note"
                value={settlementForm.comment}
                onChange={(e) => setSettlementForm({ ...settlementForm, comment: e.target.value })}
              />
            </div>
          </div>

          <div className="cc-action-row">
            <button type="button" className="btn cc-primary-action" onClick={settleLedger} disabled={saving}>
              <FaSave aria-hidden="true" />
              {saving ? "Settling..." : "Apply payment"}
            </button>
            <div className="cc-inline-note">
              Payments are allocated to the oldest pending credit rows.
            </div>
          </div>
        </div>

        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Opening credit</span>
              <h3>Add an initial balance or backdated opening amount</h3>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light cc-mini-btn"
              onClick={() => {
                const nextCustomerId = passbookCustomerId || "";
                setInitialForm({
                  customer_id: nextCustomerId,
                  amount: "",
                  fuel_type: "Opening Balance",
                  volume: "",
                  comment: "Opening balance",
                  created_at: new Date(),
                });
                fetchOpeningCreditBlock(nextCustomerId).catch((err) =>
                  console.error("Opening credit block reset error:", err)
                );
              }}
            >
              Reset
            </button>
          </div>

          <div className="cc-form-grid">
            <div>
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={initialForm.customer_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setInitialForm({ ...initialForm, customer_id: value });
                  fetchOpeningCreditBlock(value).catch((err) =>
                    console.error("Opening credit limit fetch error:", err)
                  );
                }}
              >
                <option value="">Select customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Created at</label>
              <DatePicker
                className="form-control"
                selected={initialForm.created_at}
                onChange={(d) => setInitialForm({ ...initialForm, created_at: d || new Date() })}
                showTimeSelect
                dateFormat="yyyy-MM-dd HH:mm"
                popperPlacement="bottom-start"
              />
            </div>

            <div>
              <label className="form-label">Amount</label>
              <input
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                placeholder="Opening amount"
                value={initialForm.amount}
                onChange={(e) => setInitialForm({ ...initialForm, amount: e.target.value })}
              />
              <small className="cc-limit-hint">
                {openingCreditBlock?.has_limit
                  ? `Max allowed ${money(openingCreditBlock.max_credit_amount)} | Remaining ${money(
                      openingCreditBlock.available_credit
                    )}`
                  : "No credit block set for this customer."}
              </small>
            </div>

            <div>
              <label className="form-label">Fuel type</label>
              <select
                className="form-select"
                value={initialForm.fuel_type}
                onChange={(e) => setInitialForm({ ...initialForm, fuel_type: e.target.value })}
              >
                {fuelOptions.map((fuel) => (
                  <option key={fuel} value={fuel}>
                    {fuel}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Volume</label>
              <input
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                placeholder="Litres"
                value={initialForm.volume}
                onChange={(e) => setInitialForm({ ...initialForm, volume: e.target.value })}
              />
            </div>

            <div>
              <label className="form-label">Comment</label>
              <input
                className="form-control"
                placeholder="Reason or note"
                value={initialForm.comment}
                onChange={(e) => setInitialForm({ ...initialForm, comment: e.target.value })}
              />
            </div>
          </div>

          <div className="cc-action-row">
            <button type="button" className="btn cc-primary-action" onClick={saveInitialCredit} disabled={saving}>
              <FaPlus aria-hidden="true" />
              {saving ? "Saving..." : "Done"}
            </button>
          </div>
        </div>
      </section>

      <section className="cc-card cc-timeline-card">
        <div className="cc-card-head">
          <div>
            <span>Passbook</span>
            <h3>Running ledger for {passbookCustomer?.name || "selected customer"}</h3>
          </div>
        </div>

        <div className="cc-timeline">
          {passbookRows.length ? (
            passbookRows.map((row) => (
              <PassbookTransaction key={`${row.source_type}-${row.source_id}`} row={row} />
            ))
          ) : (
            <div className="cc-empty-state">
              <FaBookOpen aria-hidden="true" />
              <strong>No passbook activity in this range</strong>
              <span>Choose another customer or date range to see the ledger flow.</span>
            </div>
          )}
        </div>

        {passbookTransactions.length > pageSize && (
          <div className="cc-pagination">
            <button
              type="button"
              className="cc-page-btn"
              disabled={safePassbookPage === 1}
              onClick={() => setPassbookPage((p) => p - 1)}
            >
              Prev
            </button>

            <div className="cc-page-list">
              {getPassbookPageNumbers().map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`cc-page-btn ${safePassbookPage === page ? "active" : ""}`}
                  onClick={() => setPassbookPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="cc-page-btn"
              disabled={safePassbookPage === passbookTotalPages}
              onClick={() => setPassbookPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
